export default {
  en: {
    title: 'Content blocks',
    subtitle: 'Images, videos, embeds (e.g. YouTube) and background audio you can show while a contact waits in queue. Once created, attach them from a segment or calendar.',
    columns: {
      name: 'Name',
      kind: 'Type',
      target: 'Target',
      order: 'Order',
      enabled: 'Status',
    },
    kinds: {
      image: 'Image',
      video: 'Video',
      iframe: 'Embed (iframe/ad)',
      audio: 'Background audio',
    },
    attach: {
      placeholder: 'Select content blocks to attach',
      segment: 'Images, videos, embeds and background audio shown to contacts while they wait in this segment\'s queue.',
      service: 'Images, videos, embeds and background audio shown to contacts while they wait for this calendar.',
    },
    form: {
      name: {
        placeholder: 'Promo video',
        hint: 'Optional name to help you identify this content block.',
        errorMax: 'The name cannot exceed 80 characters.',
      },
      kind: {
        label: 'Type',
        hint: 'What kind of content this block renders.',
        error: 'Please choose a type.',
      },
      order: {
        label: 'Order',
        hint: 'Relative order when several content blocks are attached to the same segment or calendar.',
      },
      durationSeconds: {
        label: 'Duration (seconds)',
      },
      kindSettings: {
        image: 'Image settings',
        video: 'Video settings',
        iframe: 'Embed settings',
        audio: 'Audio settings',
      },
      image: {
        url: {
          label: 'Image URL',
          error: 'The URL is required for image content blocks.',
          errorPattern: 'The URL must start with https://.',
        },
        altText: {
          label: 'Alternative text',
          errorMax: 'The alternative text cannot exceed 200 characters.',
        },
      },
      video: {
        url: {
          label: 'Video URL',
          hint: 'Direct HTTPS URL of a hosted video file (mp4). For YouTube/Vimeo use the "Embed" type instead.',
          error: 'The URL is required for video content blocks.',
          errorPattern: 'The URL must start with https://.',
        },
        posterUrl: {
          label: 'Poster URL',
          hint: 'Thumbnail shown before the video starts playing.',
          error: 'The poster URL must start with https://.',
        },
        durationSeconds: {
          hint: 'Leave empty to move to the next content block only when the video finishes playing.',
        },
        autoplay: {
          label: 'Autoplay',
        },
        loop: {
          label: 'Loop',
        },
      },
      iframe: {
        url: {
          label: 'Embed URL',
          hint: 'HTTPS URL to embed, e.g. a YouTube/Vimeo embed link or an ad unit.',
          error: 'The URL is required for embed content blocks.',
          errorPattern: 'The URL must start with https://.',
        },
        allow: {
          label: 'Allow attributes',
          placeholder: 'autoplay, encrypted-media, ...',
        },
        sandbox: {
          label: 'Sandbox attributes',
          placeholder: 'allow-scripts, allow-same-origin, ...',
        },
        durationSeconds: {
          hint: 'How long this embed is shown before rotating to the next content block (embeds cannot report when they end).',
        },
      },
      audio: {
        url: {
          label: 'Audio URL',
          hint: 'Direct HTTPS URL of a hosted audio file, played in the background while the contact waits.',
          error: 'The URL is required for audio content blocks.',
          errorPattern: 'The URL must start with https://.',
        },
        loop: {
          label: 'Loop',
        },
      },
      advanced: 'Advanced',
      enabled: {
        label: 'Enabled',
        hint: 'Disable to keep the content block from being shown without deleting it.',
      },
    },
  },
  es: {
    title: 'Bloques de contenido',
    subtitle: 'Imágenes, videos, embebidos (por ejemplo YouTube) y audio de fondo que puedes mostrar mientras un contacto espera en la fila. Una vez creados, asócialos desde un segmento o calendario.',
    columns: {
      name: 'Nombre',
      kind: 'Tipo',
      target: 'Destino',
      order: 'Orden',
      enabled: 'Estado',
    },
    kinds: {
      image: 'Imagen',
      video: 'Video',
      iframe: 'Embebido (iframe/ad)',
      audio: 'Audio de fondo',
    },
    attach: {
      placeholder: 'Selecciona los bloques de contenido a asociar',
      segment: 'Imágenes, videos, embebidos y audio de fondo que se muestran a los contactos mientras esperan en la fila de este segmento.',
      service: 'Imágenes, videos, embebidos y audio de fondo que se muestran a los contactos mientras esperan por este calendario.',
    },
    form: {
      name: {
        placeholder: 'Video promocional',
        hint: 'Nombre opcional para identificar este bloque de contenido.',
        errorMax: 'El nombre no puede superar los 80 caracteres.',
      },
      kind: {
        label: 'Tipo',
        hint: 'Qué tipo de contenido renderiza este bloque.',
        error: 'Selecciona un tipo.',
      },
      order: {
        label: 'Orden',
        hint: 'Orden relativo cuando hay varios bloques de contenido asociados al mismo segmento o calendario.',
      },
      durationSeconds: {
        label: 'Duración (segundos)',
      },
      kindSettings: {
        image: 'Configuración de imagen',
        video: 'Configuración de video',
        iframe: 'Configuración del embebido',
        audio: 'Configuración de audio',
      },
      image: {
        url: {
          label: 'URL de la imagen',
          error: 'La URL es obligatoria para bloques de tipo imagen.',
          errorPattern: 'La URL debe comenzar con https://.',
        },
        altText: {
          label: 'Texto alternativo',
          errorMax: 'El texto alternativo no puede superar los 200 caracteres.',
        },
      },
      video: {
        url: {
          label: 'URL del video',
          hint: 'URL HTTPS directa de un archivo de video alojado (mp4). Para YouTube/Vimeo usa el tipo "Embebido".',
          error: 'La URL es obligatoria para bloques de tipo video.',
          errorPattern: 'La URL debe comenzar con https://.',
        },
        posterUrl: {
          label: 'URL de la miniatura',
          hint: 'Miniatura mostrada antes de que el video comience a reproducirse.',
          error: 'La URL de la miniatura debe comenzar con https://.',
        },
        durationSeconds: {
          hint: 'Déjalo vacío para pasar al siguiente bloque solo cuando el video termine.',
        },
        autoplay: {
          label: 'Reproducción automática',
        },
        loop: {
          label: 'Repetir',
        },
      },
      iframe: {
        url: {
          label: 'URL del embebido',
          hint: 'URL HTTPS a incrustar, por ejemplo un link de embebido de YouTube/Vimeo o un anuncio.',
          error: 'La URL es obligatoria para bloques de tipo embebido.',
          errorPattern: 'La URL debe comenzar con https://.',
        },
        allow: {
          label: 'Atributos allow',
          placeholder: 'autoplay, encrypted-media, ...',
        },
        sandbox: {
          label: 'Atributos sandbox',
          placeholder: 'allow-scripts, allow-same-origin, ...',
        },
        durationSeconds: {
          hint: 'Cuánto tiempo se muestra este embebido antes de rotar al siguiente bloque (los embebidos no avisan cuándo terminan).',
        },
      },
      audio: {
        url: {
          label: 'URL del audio',
          hint: 'URL HTTPS directa de un archivo de audio alojado, reproducido de fondo mientras el contacto espera.',
          error: 'La URL es obligatoria para bloques de tipo audio.',
          errorPattern: 'La URL debe comenzar con https://.',
        },
        loop: {
          label: 'Repetir',
        },
      },
      advanced: 'Avanzado',
      enabled: {
        label: 'Activado',
        hint: 'Desactívalo para ocultar el bloque de contenido sin eliminarlo.',
      },
    },
  },
  fr: {
    title: 'Blocs de contenu',
    subtitle: 'Images, vidéos, contenus intégrés (par ex. YouTube) et audio de fond à afficher pendant qu\'un contact patiente dans la file. Une fois créés, associez-les à un segment ou un calendrier.',
    columns: {
      name: 'Nom',
      kind: 'Type',
      target: 'Cible',
      order: 'Ordre',
      enabled: 'Statut',
    },
    kinds: {
      image: 'Image',
      video: 'Vidéo',
      iframe: 'Intégration (iframe/pub)',
      audio: 'Audio de fond',
    },
    attach: {
      placeholder: 'Sélectionnez les blocs de contenu à associer',
      segment: 'Images, vidéos, contenus intégrés et audio de fond affichés aux contacts pendant qu\'ils patientent dans la file de ce segment.',
      service: 'Images, vidéos, contenus intégrés et audio de fond affichés aux contacts pendant qu\'ils patientent pour ce calendrier.',
    },
    form: {
      name: {
        placeholder: 'Vidéo promotionnelle',
        hint: 'Nom facultatif pour identifier ce bloc de contenu.',
        errorMax: 'Le nom ne peut pas dépasser 80 caractères.',
      },
      kind: {
        label: 'Type',
        hint: 'Quel type de contenu ce bloc affiche.',
        error: 'Veuillez choisir un type.',
      },
      order: {
        label: 'Ordre',
        hint: 'Ordre relatif lorsque plusieurs blocs de contenu sont associés au même segment ou calendrier.',
      },
      durationSeconds: {
        label: 'Durée (secondes)',
      },
      kindSettings: {
        image: 'Paramètres de l\'image',
        video: 'Paramètres de la vidéo',
        iframe: 'Paramètres de l\'intégration',
        audio: 'Paramètres audio',
      },
      image: {
        url: {
          label: 'URL de l\'image',
          error: 'L\'URL est obligatoire pour les blocs de type image.',
          errorPattern: 'L\'URL doit commencer par https://.',
        },
        altText: {
          label: 'Texte alternatif',
          errorMax: 'Le texte alternatif ne peut pas dépasser 200 caractères.',
        },
      },
      video: {
        url: {
          label: 'URL de la vidéo',
          hint: 'URL HTTPS directe d\'un fichier vidéo hébergé (mp4). Pour YouTube/Vimeo, utilisez le type "Intégration".',
          error: 'L\'URL est obligatoire pour les blocs de type vidéo.',
          errorPattern: 'L\'URL doit commencer par https://.',
        },
        posterUrl: {
          label: 'URL de la vignette',
          hint: 'Vignette affichée avant le début de la lecture de la vidéo.',
          error: 'L\'URL de la vignette doit commencer par https://.',
        },
        durationSeconds: {
          hint: 'Laissez vide pour passer au bloc suivant seulement à la fin de la vidéo.',
        },
        autoplay: {
          label: 'Lecture automatique',
        },
        loop: {
          label: 'Boucle',
        },
      },
      iframe: {
        url: {
          label: 'URL de l\'intégration',
          hint: 'URL HTTPS à intégrer, par exemple un lien d\'intégration YouTube/Vimeo ou une publicité.',
          error: 'L\'URL est obligatoire pour les blocs de type intégration.',
          errorPattern: 'L\'URL doit commencer par https://.',
        },
        allow: {
          label: 'Attributs allow',
          placeholder: 'autoplay, encrypted-media, ...',
        },
        sandbox: {
          label: 'Attributs sandbox',
          placeholder: 'allow-scripts, allow-same-origin, ...',
        },
        durationSeconds: {
          hint: 'Durée d\'affichage de cette intégration avant de passer au bloc suivant (les intégrations ne signalent pas leur fin).',
        },
      },
      audio: {
        url: {
          label: 'URL de l\'audio',
          hint: 'URL HTTPS directe d\'un fichier audio hébergé, joué en arrière-plan pendant que le contact patiente.',
          error: 'L\'URL est obligatoire pour les blocs de type audio.',
          errorPattern: 'L\'URL doit commencer par https://.',
        },
        loop: {
          label: 'Boucle',
        },
      },
      advanced: 'Avancé',
      enabled: {
        label: 'Activé',
        hint: 'Désactivez pour masquer le bloc de contenu sans le supprimer.',
      },
    },
  },
};
